import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", full_name: "", company_name: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Le mot de passe doit faire au moins 6 caractères.");
    setLoading(true);
    try {
      await register(form);
      toast.success("Compte créé. Bienvenue !");
      navigate("/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Inscription échouée");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-[#FAFAFA]">
      <div className="lg:col-span-2 bg-black text-white p-12 flex flex-col justify-between grain">
        <Link to="/" className="flex items-center gap-2" data-testid="signup-logo">
          <div className="w-7 h-7 klein-bg rounded-sm flex items-center justify-center">
            <span className="font-display text-white font-bold text-sm">F</span>
          </div>
          <span className="font-display font-semibold tracking-tight">FlowMind AI</span>
        </Link>
        <div>
          <span className="overline text-zinc-400">Commencer</span>
          <h2 className="font-display text-4xl font-medium tracking-tighter mt-3 mb-4">Rejoignez 400+ PME européennes.</h2>
          <p className="text-zinc-400 max-w-sm">14 jours d'essai. Aucune carte bancaire. Onboarding guidé en 3 étapes.</p>
        </div>
        <Link to="/" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors">
          <ArrowLeft size={14} /> Retour au site
        </Link>
      </div>

      <div className="lg:col-span-3 flex items-center justify-center p-8">
        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-6" data-testid="signup-form">
          <div>
            <h1 className="font-display text-3xl font-medium tracking-tight mb-2">Créer un compte</h1>
            <p className="text-sm text-zinc-600">
              Déjà client ? <Link to="/login" className="klein-text hover:underline">Se connecter</Link>
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="full_name">Nom complet</Label>
              <Input id="full_name" required value={form.full_name} onChange={onChange("full_name")} className="rounded-sm mt-2" placeholder="Camille Laurent" data-testid="signup-fullname-input"/>
            </div>
            <div>
              <Label htmlFor="email">Email professionnel</Label>
              <Input id="email" type="email" required value={form.email} onChange={onChange("email")} className="rounded-sm mt-2" placeholder="vous@entreprise.eu" data-testid="signup-email-input"/>
            </div>
            <div>
              <Label htmlFor="company">Entreprise</Label>
              <Input id="company" value={form.company_name} onChange={onChange("company_name")} className="rounded-sm mt-2" placeholder="Ex : Maison Bernard" data-testid="signup-company-input"/>
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" required value={form.password} onChange={onChange("password")} className="rounded-sm mt-2" placeholder="6 caractères minimum" data-testid="signup-password-input"/>
            </div>
          </div>

          <Button type="submit" disabled={loading} className="w-full rounded-sm bg-black text-white hover:bg-zinc-800 h-11" data-testid="signup-submit-button">
            {loading ? "Création..." : "Créer mon compte"}
          </Button>

          <p className="text-xs text-zinc-500 text-center">
            En créant un compte, vous acceptez nos <Link to="/legal#cgu" className="underline">CGU</Link> et notre <Link to="/legal#rgpd" className="underline">politique RGPD</Link>.
          </p>
        </form>
      </div>
    </div>
  );
}
